import { useCallback, useLayoutEffect, useRef, useState, type RefObject } from "react";
import AppliedFilter from "@/pages/FilterComponent/AppliedFilter";
import type { showFilters } from "@/pages/FilterComponent/FilterInterface";

type AppliedFilterMeasureProps = {
  measureRef: RefObject<HTMLDivElement | null>;
  filterData: any;
  onClearFilter: (updatedFilter: any) => void;
  defaultDateType?: string;
  showFilters?: showFilters;
};

/** Hidden sizer — place once near the top of your page header. */
export function AppliedFilterMeasure({
  measureRef,
  filterData,
  onClearFilter,
  defaultDateType,
  showFilters,
}: AppliedFilterMeasureProps) {
  return (
    <div
      ref={measureRef}
      aria-hidden="true"
      style={{
        position: "absolute",
        visibility: "hidden",
        pointerEvents: "none",
        width: "max-content",
        height: 0,
        overflow: "hidden",
      }}
    >
      <AppliedFilter
        filterData={filterData}
        onClearFilter={onClearFilter}
        {...(defaultDateType !== undefined ? { defaultDateType } : {})}
        {...(showFilters !== undefined ? { showFilters } : {})}
      />
    </div>
  );
}

type UseAppliedFilterOverflowOptions = {
  /** Re-run when filters change. Pass `filterData`. */
  watch: unknown;
  gap?: number;
};

/**
 * Attach refs to your existing header layout:
 * - `headerRef` → full header row (filter + chips + actions)
 * - `filterBtnRef` → filter button wrapper
 * - `rightRef` → right-side actions wrapper
 *
 * Then render AppliedFilter inline when `!chipsOverflow`, below header when `chipsOverflow`.
 */
export function useAppliedFilterOverflow({ watch, gap = 16 }: UseAppliedFilterOverflowOptions) {
  const headerRef = useRef<HTMLDivElement>(null);
  const filterBtnRef = useRef<HTMLDivElement>(null);
  const rightRef = useRef<HTMLDivElement>(null);
  const measureRef = useRef<HTMLDivElement>(null);
  const [chipsOverflow, setChipsOverflow] = useState(false);

  const updatePlacement = useCallback(() => {
    const header = headerRef.current;
    const filterBtn = filterBtnRef.current;
    const right = rightRef.current;
    const measure = measureRef.current;

    if (!header || !filterBtn || !right || !measure) return;

    const chipsWidth = measure.scrollWidth;
    if (chipsWidth === 0) {
      setChipsOverflow(false);
      return;
    }

    const spaceBesideFilter =
      header.clientWidth - filterBtn.offsetWidth - right.offsetWidth - gap;

    setChipsOverflow(chipsWidth > spaceBesideFilter);
  }, [gap]);

  useLayoutEffect(() => {
    updatePlacement();

    const observer = new ResizeObserver(updatePlacement);
    [headerRef.current, measureRef.current, rightRef.current, filterBtnRef.current].forEach((node) => {
      if (node) observer.observe(node);
    });

    return () => observer.disconnect();
  }, [updatePlacement, watch]);

  return {
    chipsOverflow,
    headerRef,
    filterBtnRef,
    rightRef,
    measureRef,
  };
}
